import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle2, Circle, Download, ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'compulsories', label: 'Compulsories' },
  { key: 'notes', label: 'Judges Notes' },
  { key: 'groups', label: 'Groups / Figures' },
];

export default function AnnualReviewPage() {
  const { scheduleId } = useParams();
  const navigate = useNavigate();

  const [schedule, setSchedule] = useState(null);
  const [status, setStatus] = useState(null);
  const [grades, setGrades] = useState([]);
  const [dances, setDances] = useState([]);
  const [figures, setFigures] = useState([]);
  const [activeTab, setActiveTab] = useState('compulsories');
  const [editing, setEditing] = useState(null); // grade being edited in a dialog
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const [schedsR, statusR, gradesR] = await Promise.all([
        axios.get(`${API}/content/schedules`, { withCredentials: true }),
        axios.get(`${API}/schedules/${scheduleId}/annual-review/status`, { withCredentials: true }),
        axios.get(`${API}/content/grades`, { withCredentials: true }),
      ]);
      const s = (schedsR.data || []).find(x => x.schedule_id === scheduleId);
      setSchedule(s);
      setStatus(statusR.data);
      setGrades((gradesR.data || [])
        .filter(g => g.schedule_id === scheduleId)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load annual review');
    }
  };

  useEffect(() => {
    reload();
    axios.get(`${API}/content/compulsory-dances`, { withCredentials: true })
      .then(r => setDances(r.data || [])).catch(() => {});
    axios.get(`${API}/content/figures`, { withCredentials: true })
      .then(r => setFigures(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId]);

  const danceMap = useMemo(() => Object.fromEntries(dances.map(d => [d.dance_id, d])), [dances]);
  const figureMap = useMemo(() => Object.fromEntries(figures.map(f => [f.figure_id, f])), [figures]);

  const markAllUnchanged = async () => {
    try {
      const r = await axios.post(`${API}/schedules/${scheduleId}/annual-review/mark-all-unchanged`, {}, { withCredentials: true });
      toast.success(`${r.data.marked} grade(s) marked as reviewed.`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const publish = async () => {
    try {
      await axios.post(`${API}/schedules/${scheduleId}/annual-review/publish`, {}, { withCredentials: true });
      toast.success('Published! Previous schedule archived.');
      navigate('/content');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    }
  };

  const discard = async () => {
    if (!window.confirm('Discard this annual-review draft? The current schedule will be untouched.')) return;
    try {
      await axios.post(`${API}/schedules/${scheduleId}/annual-review/discard`, {}, { withCredentials: true });
      toast.success('Draft discarded.');
      navigate('/content');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Discard failed');
    }
  };

  const downloadReport = async () => {
    try {
      const r = await axios.get(`${API}/schedules/${scheduleId}/annual-review/changes-report.docx`, {
        withCredentials: true, responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: r.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `annual-review-${schedule?.schedule_type || 'schedule'}-${schedule?.review_year || 'draft'}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the report');
    }
  };

  const openEditor = (grade) => {
    // Deep-clone so cancel is a real cancel.
    setEditing(JSON.parse(JSON.stringify(grade)));
  };

  const saveEditor = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {};
      if (activeTab === 'compulsories') {
        payload.compulsories = editing.compulsories || { dance_1_id: null, dance_2_id: null };
      } else if (activeTab === 'notes') {
        payload.judges_notes_round_one = editing.judges_notes_round_one || '';
        payload.judges_notes_round_two = editing.judges_notes_round_two || '';
      } else if (activeTab === 'groups') {
        payload.groups = (editing.groups || []).map(g => ({
          figure_1_id: g.figure_1_id, figure_1_foot: g.figure_1_foot || '',
          figure_2_id: g.figure_2_id, figure_2_foot: g.figure_2_foot || '',
          figure_3_id: g.figure_3_id, figure_3_foot: g.figure_3_foot || '',
          figure_4_id: g.figure_4_id, figure_4_foot: g.figure_4_foot || '',
        }));
      }
      await axios.patch(
        `${API}/schedules/${scheduleId}/annual-review/grades/${editing.grade_id}`,
        payload,
        { withCredentials: true }
      );
      toast.success('Saved.');
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const markReviewed = async (gradeId) => {
    try {
      await axios.post(`${API}/schedules/${scheduleId}/annual-review/grades/${gradeId}/mark-reviewed`, {}, { withCredentials: true });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (!schedule || !status) return <div className="p-8 text-muted-foreground">Loading annual review…</div>;

  const pct = status.total_grades > 0
    ? Math.round((status.reviewed_grades / status.total_grades) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="annual-review-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/content')} className="text-sm text-muted-foreground hover:text-black" data-testid="back-to-content">
          <ArrowLeft className="h-4 w-4 inline" /> Back
        </button>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
          Annual Review — {schedule.schedule_type} {schedule.review_year && <span className="text-stone-500">({schedule.review_year})</span>}
        </h1>
        {schedule.is_draft && <Badge className="bg-amber-100 text-amber-800">Draft</Badge>}
      </div>

      {/* Progress + actions */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm">
            <strong>{status.reviewed_grades}</strong> of <strong>{status.total_grades}</strong> grades reviewed
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadReport} data-testid="download-report-btn">
              <Download className="h-4 w-4 mr-2" /> Changes report (.docx)
            </Button>
            <Button variant="outline" onClick={markAllUnchanged} disabled={status.complete} data-testid="mark-all-unchanged-btn">
              Mark remaining as unchanged
            </Button>
            <Button className="btn-primary" onClick={publish} disabled={!status.complete} data-testid="publish-btn">
              Publish for {schedule.review_year}
            </Button>
            <Button variant="destructive" onClick={discard} data-testid="discard-draft-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Discard
            </Button>
          </div>
        </div>
        <div className="w-full h-2 bg-stone-200 rounded overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-stone-200">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 border-b-2 ${activeTab === t.key ? 'border-emerald-600 text-emerald-700 font-semibold' : 'border-transparent text-muted-foreground'}`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Grade list per tab */}
      <Card className="p-4">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200 text-left text-sm">
                <th className="py-2 px-2 w-8"></th>
                <th className="py-2 px-2">Grade</th>
                <th className="py-2 px-2">Content Preview</th>
                <th className="py-2 px-2 w-40 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {grades.map(g => {
                const reviewed = !!g.annual_review_touched_at;
                let preview = '';
                if (activeTab === 'compulsories') {
                  const parts = [];
                  if (g.compulsories?.dance_1_id) parts.push(danceMap[g.compulsories.dance_1_id]?.dance_name || '?');
                  if (g.compulsories?.dance_2_id) parts.push(danceMap[g.compulsories.dance_2_id]?.dance_name || '?');
                  preview = parts.join(' · ') || '—';
                } else if (activeTab === 'notes') {
                  const r1 = (g.judges_notes_round_one || '').slice(0, 90);
                  preview = r1 ? `R1: ${r1}${g.judges_notes_round_one?.length > 90 ? '…' : ''}` : '—';
                } else if (activeTab === 'groups') {
                  const grps = (g.groups || []).map((grp, i) => {
                    const figs = [grp.figure_1_id, grp.figure_2_id, grp.figure_3_id, grp.figure_4_id]
                      .filter(Boolean)
                      .map(fid => figureMap[fid]?.figure_name || '?');
                    return `G${i + 1}: ${figs.join(', ')}`;
                  });
                  preview = grps.join(' | ') || '—';
                }
                return (
                  <tr key={g.grade_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`grade-row-${g.grade_id}`}>
                    <td className="py-3 px-2">
                      {reviewed
                        ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        : <Circle className="h-4 w-4 text-stone-300" />}
                    </td>
                    <td className="py-3 px-2 font-medium">{g.name}</td>
                    <td className="py-3 px-2 text-sm text-muted-foreground truncate max-w-md">{preview}</td>
                    <td className="py-3 px-2 text-center">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => openEditor(g)} className="text-emerald-700 hover:underline text-sm" data-testid={`edit-grade-${g.grade_id}`}>
                          Edit
                        </button>
                        {!reviewed && (
                          <button onClick={() => markReviewed(g.grade_id)} className="text-stone-500 hover:underline text-sm" data-testid={`mark-reviewed-${g.grade_id}`}>
                            No change
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl" data-testid="edit-grade-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.name} — {TABS.find(t => t.key === activeTab)?.label}</DialogTitle>
          </DialogHeader>
          {editing && activeTab === 'compulsories' && (
            <div className="space-y-3">
              <div>
                <Label>Dance 1</Label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={editing.compulsories?.dance_1_id || ''}
                  onChange={(e) => setEditing({
                    ...editing,
                    compulsories: { ...(editing.compulsories || {}), dance_1_id: e.target.value || null },
                  })}
                  data-testid="dance-1-select"
                >
                  <option value="">-- none --</option>
                  {dances.map(d => <option key={d.dance_id} value={d.dance_id}>{d.dance_name}</option>)}
                </select>
              </div>
              <div>
                <Label>Dance 2</Label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={editing.compulsories?.dance_2_id || ''}
                  onChange={(e) => setEditing({
                    ...editing,
                    compulsories: { ...(editing.compulsories || {}), dance_2_id: e.target.value || null },
                  })}
                  data-testid="dance-2-select"
                >
                  <option value="">-- none --</option>
                  {dances.map(d => <option key={d.dance_id} value={d.dance_id}>{d.dance_name}</option>)}
                </select>
              </div>
            </div>
          )}
          {editing && activeTab === 'notes' && (
            <div className="space-y-3">
              <div>
                <Label>Round 1 — Judges Notes / Programme Details</Label>
                <Textarea rows={6} value={editing.judges_notes_round_one || ''}
                          onChange={(e) => setEditing({ ...editing, judges_notes_round_one: e.target.value })}
                          data-testid="notes-r1" />
              </div>
              <div>
                <Label>Round 2 — Judges Notes / Programme Details</Label>
                <Textarea rows={6} value={editing.judges_notes_round_two || ''}
                          onChange={(e) => setEditing({ ...editing, judges_notes_round_two: e.target.value })}
                          data-testid="notes-r2" />
              </div>
            </div>
          )}
          {editing && activeTab === 'groups' && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {(editing.groups || []).map((grp, gi) => (
                <div key={gi} className="border rounded p-3">
                  <div className="font-semibold mb-2">Group {gi + 1}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[1, 2, 3, 4].map(fi => (
                      <div key={fi} className="flex gap-2 items-center">
                        <select
                          className="flex-1 border rounded px-2 py-1 text-sm"
                          value={grp[`figure_${fi}_id`] || ''}
                          onChange={(e) => {
                            const newGroups = [...editing.groups];
                            newGroups[gi] = { ...newGroups[gi], [`figure_${fi}_id`]: e.target.value || null };
                            setEditing({ ...editing, groups: newGroups });
                          }}
                          data-testid={`group-${gi}-fig-${fi}`}
                        >
                          <option value="">-- none --</option>
                          {figures.map(f => <option key={f.figure_id} value={f.figure_id}>{f.figure_name}</option>)}
                        </select>
                        <select
                          className="border rounded px-2 py-1 text-sm w-20"
                          value={grp[`figure_${fi}_foot`] || ''}
                          onChange={(e) => {
                            const newGroups = [...editing.groups];
                            newGroups[gi] = { ...newGroups[gi], [`figure_${fi}_foot`]: e.target.value };
                            setEditing({ ...editing, groups: newGroups });
                          }}
                        >
                          <option value="">foot</option>
                          <option value="L">L</option>
                          <option value="R">R</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {(!editing.groups || editing.groups.length === 0) && (
                <div className="text-sm text-muted-foreground">This grade has no groups.</div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="btn-primary" onClick={saveEditor} disabled={saving} data-testid="save-edit-btn">
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
